import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button, Badge, Input, Label } from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { useListParams } from "hooks/useListParams"
import {
  useAccountCustomerRefunds,
  useAccountCustomerRefund,
  useDeleteAccountCustomerRefund,
} from "hooks/useAccountCustomerRefund"
import { useCustomers } from "hooks/useCustomer"
import { useAccounts } from "hooks/useAccountEntity"
import { formatReadableDate } from "utils/formatDate"
import { formatCurrency } from "utils/formatCurrency"
import AccountFilterPanel from "pages/AccountPages/utils/AccountFilterPanel"

import AccountCustomerRefundAddEdit from "./AccountCustomerRefundAddEdit"
import AccountCustomerRefundHeader from "./AccountCustomerRefundHeader"
import AccountCustomerRefundView from "./AccountCustomerRefundView"

const AccountCustomerRefundList = () => {
  document.title = "Account Customer Refund List"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [viewModal, setViewModal] = useState(false)
  const [selectedViewId, setSelectedViewId] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [filters, setFilters] = useState({ date_from: "", date_to: "", customer_id: "", account_id: "" })

  const { params, setPage, setSearch, resetParams, hasActiveFilters } = useListParams({ page: 1, search: "" })

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }, [])

  const handleFilterReset = useCallback(() => {
    setFilters({ date_from: "", date_to: "", customer_id: "", account_id: "" })
  }, [])

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return {
      page,
      per_page,
      search,
      ...(filters.date_from && { date_from: filters.date_from }),
      ...(filters.date_to && { date_to: filters.date_to }),
      ...(filters.customer_id && { customer_id: filters.customer_id }),
      ...(filters.account_id && { account_id: filters.account_id }),
    }
  }, [params, filters])

  const { data: responseData, isLoading, refetch, isRefetching } = useAccountCustomerRefunds(queryParams)
  const { data: viewResponse, isLoading: isViewLoading } = useAccountCustomerRefund(selectedViewId, viewModal)
  const { mutateAsync: deleteItem, isPending: deleteLoading } = useDeleteAccountCustomerRefund()

  const { data: customerResponse } = useCustomers({ all: true, per_page: 200 })
  const { data: accountResponse } = useAccounts({ all: true, per_page: 200 })
  const customerList = customerResponse?.data?.data || customerResponse?.data || []
  const accountList = accountResponse?.data?.data || accountResponse?.data || []

  const toggleViewModal = useCallback(() => {
    setViewModal(prev => !prev)
    if (viewModal) setSelectedViewId(null)
  }, [viewModal])

  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) {
      setSelectedData(null)
      setIsEdit(false)
    }
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedData(null)
    setIsEdit(false)
    setModal(true)
  }

  const handleEditClick = item => {
    setSelectedData(item)
    setIsEdit(true)
    setModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleViewClick = item => {
    setSelectedViewId(item?.id || null)
    setViewModal(true)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Customer refund deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch {
      toast.error("Failed to delete customer refund.")
    }
  }

  const columns = useMemo(
    () => [
      { header: "ID", accessorKey: "id" },
      { header: "Customer", cell: ({ row }) => row.original.customer?.name || "-" },
      {
        header: "Amount",
        cell: ({ row }) => {
          const currency = row.original.account?.currency || "BDT"
          return (
            <div className="d-flex flex-wrap gap-1 align-items-center">
              <Badge color="danger">{formatCurrency(row.original.amount || 0, currency)}</Badge>
              {row.original.is_reversed && <Badge color="secondary">Reversed</Badge>}
            </div>
          )
        },
      },
      {
        header: "Method",
        cell: ({ row }) => row.original.payment_method?.name || row.original.paymentMethod?.name || "-",
      },
      {
        header: "Refund From",
        cell: ({ row }) => row.original.account?.name || "-",
      },
      {
        header: "Reason",
        cell: ({ row }) => row.original.reason || "-",
      },
      {
        header: "Date",
        cell: ({ row }) => (row.original.refund_date ? formatReadableDate(row.original.refund_date) : "-"),
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <ul className="list-unstyled hstack gap-1 mb-0">
            <li>
              <Button color="primary" size="sm" onClick={() => handleViewClick(row.original)} id={`view-account-customer-refund-${row.original.id}`}>
                <i className="mdi mdi-eye-outline" />
              </Button>
              <UncontrolledTooltip placement="top" target={`view-account-customer-refund-${row.original.id}`}>View</UncontrolledTooltip>
            </li>
            <CanView access={[ACCESS.ACCOUNT_CUSTOMER_REFUND.EDIT]}>
              <li>
                <Button color="info" size="sm" onClick={() => handleEditClick(row.original)} id={`edit-account-customer-refund-${row.original.id}`}>
                  <i className="mdi mdi-pencil-outline" />
                </Button>
                <UncontrolledTooltip placement="top" target={`edit-account-customer-refund-${row.original.id}`}>Edit</UncontrolledTooltip>
              </li>
            </CanView>
            <CanView access={[ACCESS.ACCOUNT_CUSTOMER_REFUND.DELETE]}>
              <li>
                <Button color="danger" size="sm" onClick={() => handleDeleteClick(row.original)} id={`delete-account-customer-refund-${row.original.id}`}>
                  <i className="mdi mdi-delete-outline" />
                </Button>
                <UncontrolledTooltip placement="top" target={`delete-account-customer-refund-${row.original.id}`}>Delete</UncontrolledTooltip>
              </li>
            </CanView>
          </ul>
        ),
      },
    ],
    []
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  return (
    <PageWrapper title="Account Customer Refund" breadcrumbItem="Account Customer Refund List">
      <AccountCustomerRefundHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={refetch}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
      />

      <div className="pb-3" />

      <DeleteModal show={deleteModal} onDeleteClick={handleDelete} loading={deleteLoading} onCloseClick={() => setDeleteModal(false)} />

      <AccountFilterPanel filters={filters} onChange={handleFilterChange} onReset={handleFilterReset}>
        <Col xs={12} sm={6} md={3}>
          <Label className="mb-1 small fw-semibold">Customer</Label>
          <Input type="select" bsSize="sm" value={filters.customer_id || ""} onChange={e => handleFilterChange("customer_id", e.target.value)}>
            <option value="">All Customers</option>
            {customerList.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Input>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Label className="mb-1 small fw-semibold">Account</Label>
          <Input type="select" bsSize="sm" value={filters.account_id || ""} onChange={e => handleFilterChange("account_id", e.target.value)}>
            <option value="">All Accounts</option>
            {accountList.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Input>
        </Col>
      </AccountFilterPanel>

      <Row className="mb-3">
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer columns={columns} data={data} isPagination={true} paginationInfo={paginationInfo} onPageChange={setPage} loading={isLoading || isRefetching} />

      <AccountCustomerRefundView
        modal={viewModal}
        toggleModal={toggleViewModal}
        refund={viewResponse?.data}
        loading={isViewLoading}
      />

      <AccountCustomerRefundAddEdit modal={modal} isEdit={isEdit} editedData={selectedData} toggleModal={toggleModal} />
    </PageWrapper>
  )
}

export default AccountCustomerRefundList
